import { Link } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
  BookMarked,
  Lightbulb,
  Users,
  Network,
  FileText,
  BookOpen,
  Newspaper,
  Linkedin,
  ScrollText,
} from 'lucide-react'

const quickLinks = [
  { to: '/notebook', icon: BookMarked, label: 'Notebook', description: 'Collect and organize sources' },
  { to: '/solutions', icon: Lightbulb, label: 'Solutions', description: 'Build and manage solutions' },
  { to: '/council', icon: Users, label: 'Council', description: 'Multi-agent deliberation' },
  { to: '/knowledge-graph', icon: Network, label: 'KG Browser', description: 'Explore the knowledge graph' },
]

interface TrackerStats {
  total: number
  whitepapers: number
  blogs: number
  books: number
  linkedin: number
}

export function DashboardPage() {
  const [stats, setStats] = useState<TrackerStats | null>(null)

  useEffect(() => {
    fetch('/api/tracker/stats', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data: TrackerStats) => setStats(data))
      .catch(() => {/* leave null — tiles show '--' */})
  }, [])

  const fmt = (n: number | undefined) => (n == null ? '--' : n.toLocaleString())

  const tiles = [
    { icon: FileText,   label: 'Documents',    value: fmt(stats?.total) },
    { icon: ScrollText, label: 'White Papers',  value: fmt(stats?.whitepapers) },
    { icon: Newspaper,  label: 'Blogs',         value: fmt(stats?.blogs) },
    { icon: BookOpen,   label: 'Books',         value: fmt(stats?.books) },
    { icon: Linkedin,   label: 'LinkedIn',      value: fmt(stats?.linkedin) },
  ]

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-display font-black tracking-wide text-gray-900 dark:text-white uppercase">
          DOCTRINE
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Document generation and knowledge orchestration platform
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-800 p-5"
          >
            <div className="flex items-center gap-3">
              <tile.icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <span className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {tile.label}
              </span>
            </div>
            <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
              {tile.value}
            </p>
          </div>
        ))}
      </div>

      {/* Quick Links */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-4">
          Quick Links
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-800 p-5 hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
            >
              <link.icon className="w-6 h-6 text-primary-500 dark:text-primary-400 mb-3" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                {link.label}
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {link.description}
              </p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
